import React, { useState, useRef, useEffect } from 'react';
import { Avatar, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Radio, RadioGroup, FormControlLabel, CircularProgress, Typography, Box, Paper, Chip } from '@mui/material';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import VideoCallOutlinedIcon from '@mui/icons-material/VideoCallOutlined';
import GifBoxOutlinedIcon from '@mui/icons-material/GifBoxOutlined';
import PollOutlinedIcon from '@mui/icons-material/PollOutlined';
import SentimentSatisfiedAltOutlinedIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './TweetBox.css';
import { useAuth } from '../contexts/AuthContext';
import { apiService, safeApiCall, isPostIdDeleted, removeFromDeletedPostIds } from '../utils/api';
import { normalizeImageUrl } from './Post';
import config from '../config';
import { formatCompleteText } from '../utils/textUtils';
import { format, addDays, parseISO, isValid } from 'date-fns';
import { ja } from 'date-fns/locale';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';

// GIF検索結果のモックデータ
const mockGifs = [
  'https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExbmdwcTNhY3A3YWJ0YXhsZWtvazA2ZGNrdXQ1a3UzOGYzd2hkbnFqcyZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/GeimqsH0TLDt4tScGw/giphy.gif',
  'https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExMWU0ZnNvZWdibXVsYnpzYmpubzE3cXdncmZoNjN0dHN6bjVtcXN0MCZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/SvFocn0wNMx0iv2rYz/giphy.gif',
  'https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExN2ptdjlleTdnNzhzZDFqM21mZW1jNGxiajJqZHo4Mjg0MWExbHcxNSZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/kaq6GnxDlJaBq/giphy.gif',
  'https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExOG52ZmN6c3owbmJzOGt5b2V0dmJnM2FobmQ5Y3hkdjlicGIyM2d1NiZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/3oEjHAUOqG3lSS0f1C/giphy.gif',
  'https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExMDhqeGp2OGUyeWdmNmM3MHgyMjg3OHd1OWQwazNpdzBrcm11NGEweSZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/MM0Jrc8BHKx3y/giphy.gif',
  'https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExcWJ0dHlubWRia2lqcG1iNmNvYXppaXgwcTNoNHF0amFpMDJrMGVhaCZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/xT9IgG50Fb7Mi0prBC/giphy.gif'
];

// 基本的な絵文字セット
const basicEmojis = [
  '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃',
  '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙',
  '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔',
  '👍', '👎', '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '👋',
  '🎉', '✨', '🔥', '💯', '⭐', '🌟', '💫', '💥', '💢', '💦'
];

function TweetBox({ addPost, inModal = false }) {
  const { isLoggedIn, currentUser, checkPermission, isInitialized } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const textAreaRef = useRef(null);
  const textAreaWrapperRef = useRef(null);
  const buttonRef = useRef(null);
  
  // 文字数制限の定数
  const MAX_TWEET_LENGTH = 200;
  
  // 文字数をカウントする関数（マルチバイト文字対応）
  const getCharacterCount = (text) => {
    if (!text) return 0;
    // 日本語などのマルチバイト文字も1文字として正確にカウント
    return [...text].length;
  };
  
  // 投稿関連の状態
  const [postText, setPostText] = useState('');
  const [postImage, setPostImage] = useState('');
  const [postVideo, setPostVideo] = useState('');
  const [loading, setLoading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [userProfile, setUserProfile] = useState({});
  const [showPollDialog, setShowPollDialog] = useState(false);
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [pollDuration, setPollDuration] = useState("1d");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifDialog, setShowGifDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [location, setLocation] = useState('');
  const [hasExternalUrl, setHasExternalUrl] = useState(false);
  const [showUrlWarning, setShowUrlWarning] = useState(false);
  
  // 文字数関連の計算
  const currentCharCount = getCharacterCount(postText);
  const remainingChars = MAX_TWEET_LENGTH - currentCharCount;
  const isOverLimit = currentCharCount > MAX_TWEET_LENGTH;
  
  // シフト選択関連の状態
  const [showShiftDialog, setShowShiftDialog] = useState(false);
  const [selectedShiftDates, setSelectedShiftDates] = useState([]);
  const [shiftsData, setShiftsData] = useState([]);
  const [loadingShifts, setLoadingShifts] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // メンション関連の状態
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionSelectionStart, setMentionSelectionStart] = useState(0);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionDropdownPosition, setMentionDropdownPosition] = useState({ top: 0, left: 0 });

  // 投稿ユーザーかどうかチェック
  const isPosterUser = currentUser && (currentUser.role === 'poster' || currentUser.user_type === 'poster');

  // 投稿権限チェック
  const canPost = checkPermission('POST');
  
  // ユーザープロファイル情報を取得
  useEffect(() => {
    const fetchUserProfile = async () => {
      // isInitializedを確認するため、AuthContextからこの値を取得する
      if (isLoggedIn && isInitialized) {
        try {
          const response = await safeApiCall.get('/api/users/me');
          console.log('TweetBox: ユーザープロファイル取得成功:', response.data);
          setUserProfile(response.data);
        } catch (err) {
          console.error('TweetBox: ユーザープロファイル取得エラー:', err);
          // 401エラーの場合でも、現在のユーザー情報を使用することで機能を維持する
          if (err?.response?.status === 401 && currentUser) {
            console.log('TweetBox: currentUserをフォールバックとして使用:', currentUser);
            setUserProfile(currentUser);
          }
        }
      } else if (isLoggedIn && currentUser) {
        // 初期化が完了していない場合は、currentUserをそのまま使用
        console.log('TweetBox: 初期化未完了のため currentUser を使用:', currentUser);
        setUserProfile(currentUser);
      } else {
        console.log('TweetBox: ユーザープロファイル設定条件不適合:', { isLoggedIn, currentUser, isInitialized });
      }
    };
    
    fetchUserProfile();
  }, [isLoggedIn, currentUser, isInitialized]);

  // デバッグ用：userProfileとcurrentUserの変更を監視
  useEffect(() => {
    console.log('TweetBox: userProfile変更:', {
      userProfile,
      avatar: userProfile?.avatar,
      currentUserAvatar: currentUser?.avatar
    });
  }, [userProfile, currentUser]);
  
  // シフト情報を取得
  useEffect(() => {
    const fetchUserShifts = async () => {
      if (isLoggedIn && isPosterUser && currentUser) {
        try {
          setLoadingShifts(true);
          const response = await apiService.get('/api/shifts/me', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          setShiftsData(response.data?.shifts || []);
        } catch (err) {
          
        } finally {
          setLoadingShifts(false);
        }
      }
    };
    
    fetchUserShifts();
  }, [isLoggedIn, isPosterUser, currentUser]);
  
  // ファイルアップロード処理
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPostImage(e.target.result);
        setPostVideo(''); // 画像がアップロードされたら動画をリセット
      };
      reader.readAsDataURL(file);
    }
  };

  // 動画アップロード処理
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // サイズ関連のしきい値
    const TARGET_SIZE = 10 * 1024 * 1024;      // 目標: 10MB以内に収める
    const ALLOWED_MAX_SIZE = 15 * 1024 * 1024; // 許容上限: 画質が著しく下がる場合は15MBまで緩和
    const INPUT_MAX_SIZE = 200 * 1024 * 1024;  // 入力の安全上限(これ以上は圧縮処理も現実的でないため拒否)

    // 圧縮後のBlob/元ファイルをDataURLに変換してstateへ反映する共通処理
    const applyVideo = (blobOrFile) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPostVideo(ev.target.result);
        setPostImage(''); // 動画がアップロードされたら画像をリセット
        setCompressionProgress(100);
        setTimeout(() => setCompressing(false), 800);
      };
      reader.onerror = () => {
        alert('動画の読み込みに失敗しました');
        setCompressing(false);
        e.target.value = '';
      };
      reader.readAsDataURL(blobOrFile);
    };

    try {
      // 入力の安全上限チェック（極端に巨大なファイルは拒否）
      if (file.size > INPUT_MAX_SIZE) {
        alert(`動画ファイルが大きすぎます（${(file.size / (1024 * 1024)).toFixed(1)}MB）。\n200MB以下の動画をアップロードしてください。`);
        e.target.value = '';
        return;
      }

      // ビデオ時間を確認する
      const video = document.createElement('video');
      video.preload = 'metadata';

      let videoUrl = null;
      try {
        videoUrl = URL.createObjectURL(file);
        video.src = videoUrl;

        await new Promise((resolve, reject) => {
          video.onloadedmetadata = resolve;
          video.onerror = () => {
            reject(new Error('動画メタデータの読み込みに失敗しました'));
          };
          setTimeout(() => reject(new Error('動画メタデータの読み込みがタイムアウトしました')), 5000);
        });

        // 動画の時間制限（30秒）
        if (video.duration > 30) {
          alert('動画は30秒以内である必要があります');
          e.target.value = '';
          return;
        }

        // 目標サイズ以内ならそのまま採用
        if (file.size <= TARGET_SIZE) {
          applyVideo(file);
          return;
        }

        // ここから自動圧縮フロー
        // 目標(10MB)に収めるため、ビットレート/解像度を段階的に下げて再試行する
        setCompressing(true);
        setCompressionProgress(0);

        // 目標に収まるおおよそのビットレートを動画長から逆算（8bit=1byte換算、コンテナ分の余裕を見て0.9掛け）
        const bytesToBitrate = (bytes) =>
          Math.max(300000, Math.floor((bytes * 8 * 0.9) / Math.max(video.duration, 1)));

        // 段階的な圧縮設定（画質優先→段階的に落とす）
        const stages = [
          { bitrate: Math.min(1500000, bytesToBitrate(TARGET_SIZE)), maxDimension: 720, fps: 30 },
          { bitrate: bytesToBitrate(TARGET_SIZE), maxDimension: 640, fps: 24 },
          { bitrate: Math.floor(bytesToBitrate(TARGET_SIZE) * 0.7), maxDimension: 480, fps: 24 },
        ];

        let best = null; // これまでで最小のBlob
        for (let i = 0; i < stages.length; i++) {
          setCompressionProgress(0);
          let blob = null;
          try {
            blob = await compressVideo(file, video.duration, stages[i]);
          } catch (err) {
            blob = null;
          }
          if (!blob) continue;

          if (!best || blob.size < best.size) best = blob;

          // 目標(10MB)に収まったら即採用
          if (blob.size <= TARGET_SIZE) {
            applyVideo(blob);
            return;
          }
        }

        // 目標には収まらなかったが、許容上限(15MB)以内なら「画質を下げて」採用
        if (best && best.size <= ALLOWED_MAX_SIZE) {
          applyVideo(best);
          return;
        }

        // 圧縮しても15MBを超える場合はエラー
        setCompressing(false);
        const bestMb = best ? (best.size / (1024 * 1024)).toFixed(1) : (file.size / (1024 * 1024)).toFixed(1);
        alert(`動画を圧縮しましたが、まだサイズが大きすぎます（約${bestMb}MB）。\n動画を短くするか、より低画質・低解像度の動画をお試しください（上限15MB）。`);
        e.target.value = '';
      } catch (error) {
        setCompressing(false);
        alert('動画ファイルの読み込みに失敗しました。サポートされている形式の動画をアップロードしてください。');
        e.target.value = '';
      } finally {
        if (videoUrl) {
          URL.revokeObjectURL(videoUrl);
        }
      }
    } catch (error) {
      setCompressing(false);
      alert('動画のアップロードに失敗しました。別のファイル形式を試してください。');
      e.target.value = '';
    }
  };

  // 動画圧縮処理
  const compressVideo = async (videoFile, duration, opts = {}) => {
    // 圧縮パラメータ（呼び出し側から段階的に調整可能）
    const targetBitrate = opts.bitrate || 1000000; // 目標ビットレート (デフォルト1Mbps)
    const maxDimension = opts.maxDimension || 640;  // 最大辺サイズ
    const targetFPS = opts.fps || 24;               // 目標フレームレート
    return new Promise((resolve, reject) => {
      // MediaRecorderがサポートされているか確認
      if (!window.MediaRecorder) {
        
        return resolve(null);
      }
      
      // 動画要素の作成
      const video = document.createElement('video');
      video.muted = true;
      video.autoplay = true;
      video.playsInline = true;
      
      // 動画のソースを設定
      let videoUrl;
      try {
        videoUrl = URL.createObjectURL(videoFile);
        video.src = videoUrl;
        
        // キャンバス要素の作成
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        const chunksArray = []; // 圧縮された動画チャンクを保存する配列
        
        // 動画の読み込み完了時の処理
        video.onloadedmetadata = () => {
          // 縦横比を維持したままキャンバスサイズを設定
          let targetWidth, targetHeight;
          if (video.videoWidth > video.videoHeight) {
            // 横長動画
            targetWidth = maxDimension;
            targetHeight = Math.floor(video.videoHeight * (maxDimension / video.videoWidth));
          } else {
            // 縦長動画
            targetHeight = maxDimension;
            targetWidth = Math.floor(video.videoWidth * (maxDimension / video.videoHeight));
          }
          
          // キャンバスサイズを設定
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          
          try {
            // キャンバスからストリームを取得
            const canvasStream = canvas.captureStream(targetFPS);
            
            // 音声トラックを追加
            let stream = canvasStream;
            
            // 音声トラックを取得して追加を試みる
            if (videoFile.type.includes('audio') || videoFile.type.includes('video')) {
              try {
                // 元の動画から音声トラックを取得
                const audioTracks = video.captureStream().getAudioTracks();
                
                if (audioTracks && audioTracks.length > 0) {
                  // 音声トラックを追加したストリームを作成
                  stream = new MediaStream([
                    ...canvasStream.getVideoTracks(),
                    ...audioTracks
                  ]);
                  
                }
              } catch (audioErr) {
                
              }
            }
            
            // MediaRecorderの設定
            const options = {
              videoBitsPerSecond: targetBitrate,
              mimeType: 'video/webm;codecs=vp8' // WebMコーデックを指定
            };
            
            // ブラウザ互換性のためにmimeTypeをチェック
            if (!MediaRecorder.isTypeSupported(options.mimeType)) {
              options.mimeType = 'video/webm'; // コーデックなしで試す
              
              // これもサポートされていない場合
              if (!MediaRecorder.isTypeSupported(options.mimeType)) {
                delete options.mimeType; // mimeTypeを指定せず、ブラウザのデフォルトに任せる
              }
            }
            
            // MediaRecorderを初期化
            const recorder = new MediaRecorder(stream, options);
            
            // データが利用可能になった時の処理
            recorder.ondataavailable = (e) => {
              if (e.data && e.data.size > 0) {
                chunksArray.push(e.data);
              }
            };
            
            // 録画が終了した時の処理
            recorder.onstop = () => {
              // 録画した動画のBlobを作成
              const blob = new Blob(chunksArray, { type: 'video/webm' });
              resolve(blob);
              
              // リソース解放
              URL.revokeObjectURL(videoUrl);
              video.pause();
              video.src = '';
              stream.getTracks().forEach(track => track.stop());
            };
            
            // 録画の進捗状況を定期的に更新
            const updateInterval = setInterval(() => {
              const progress = Math.min(Math.round((video.currentTime / duration) * 100), 95);
              setCompressionProgress(progress);
            }, 200);
            
            // 録画開始
            recorder.start(100); // 100ms間隔でデータチャンクを生成
            
            // 動画を再生
            video.play();
            
            // 動画の描画と録画の処理
            const processVideo = () => {
              // 動画が終了、一時停止、または不足している場合は録画を停止
              if (video.ended || video.paused || !video.videoWidth) {
                recorder.stop();
                clearInterval(updateInterval);
                return;
              }
              
              // 動画フレームをキャンバスに描画
              ctx.drawImage(video, 0, 0, targetWidth, targetHeight);
              
              // 次のフレームをリクエスト
              requestAnimationFrame(processVideo);
            };
            
            // 描画処理を開始
            processVideo();
            
            // 安全のためにタイムアウトを設定（処理時間が長すぎる場合に停止）
            setTimeout(() => {
              if (recorder.state === 'recording') {
                recorder.stop();
                clearInterval(updateInterval);
              }
            }, duration * 1000 * 1.5); // 動画長の1.5倍の時間でタイムアウト
          } catch (error) {
            
            resolve(null); // エラーが発生した場合、nullを返して元のファイルを使用
            setCompressing(false);
            URL.revokeObjectURL(videoUrl);
          }
        };
        
        // エラー発生時の処理
        video.onerror = () => {
          
          URL.revokeObjectURL(videoUrl);
          resolve(null);
          setCompressing(false);
        };
      } catch (error) {
        
        if (videoUrl) {
          URL.revokeObjectURL(videoUrl);
        }
        setCompressing(false);
        resolve(null); // エラー時にnullを返す
      }
    });
  };

  // URL検出用の正規表現
  const urlRegex = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/;
  
  // 許可されたドメインのリスト
  const allowedDomains = ['localhost', '127.0.0.1', 'example.com'];
  
  // URLが外部URLかどうかチェック
  const isExternalUrl = (url) => {
    if (!url) return false;
    
    // URLからドメイン部分を抽出
    let domain;
    try {
      if (url.startsWith('http')) {
        domain = new URL(url).hostname;
      } else if (url.startsWith('www.')) {
        domain = url.substring(4).split('/')[0];
      } else {
        domain = url.split('/')[0];
      }
    } catch (e) {
      return false;
    }
    
    // 許可されたドメインかどうかチェック
    return !allowedDomains.some(allowed => domain.includes(allowed));
  };
  
  // テキスト入力からURLを検出
  const detectUrls = (text) => {
    const matches = text.match(new RegExp(urlRegex, 'gi'));
    if (matches) {
      const external = matches.some(url => isExternalUrl(url));
      setHasExternalUrl(external);
    } else {
      setHasExternalUrl(false);
    }
  };
  
  // ユーザー検索処理
  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setMentionResults([]);
      return;
    }
    
    try {
      const response = await safeApiCall.get(`/api/social/search/users?q=${query}`);
      
      if (response.data && response.data.users) {
        setMentionResults(response.data.users.slice(0, 5)); // 最大5件表示
      }
    } catch (err) {
      
      // エラー時はダミーデータを表示
      setMentionResults([
        { username: 'user1', display_name: 'ユーザー1', avatar: null },
        { username: 'user2', display_name: 'ユーザー2', avatar: null },
        { username: 'admin', display_name: '管理者', avatar: null }
      ]);
    }
  };
  
  // メンション選択処理
  const selectMention = (username) => {
    if (mentionSelectionStart === null) return;
    
    // @以降のクエリ部分をユーザー名に置き換える
    const beforeQuery = postText.substring(0, mentionSelectionStart);
    const afterQuery = postText.substring(mentionSelectionStart + mentionQuery.length + 1);
    const newText = `${beforeQuery}@${username} ${afterQuery}`;
    
    setPostText(newText);
    setShowMentionDropdown(false);
    setMentionQuery('');
    setMentionSelectionStart(null);
  };
  
  // メンションドロップダウンの位置を計算
  const calculateMentionDropdownPosition = (selectionStart) => {
    if (!textAreaWrapperRef.current || selectionStart === null) return;
    
    const textArea = textAreaRef.current;
    if (!textArea) return;
    
    // カーソル位置までのテキストを取得
    const textBeforeCursor = postText.substring(0, selectionStart);
    
    // 行と列の位置を計算
    const lines = textBeforeCursor.split('\n');
    const lineCount = lines.length;
    const colPosition = lines[lineCount - 1].length;
    
    // テキストエリアの位置情報
    const textAreaRect = textArea.getBoundingClientRect();
    const lineHeight = parseInt(window.getComputedStyle(textArea).lineHeight);
    
    // ドロップダウンの位置を計算
    const top = lineHeight * (lineCount - 1) + 30; // テキストエリアの上からのオフセット
    const left = colPosition * 8; // 文字幅の概算
    
    setMentionDropdownPosition({ top, left });
  };

  // 入力テキスト変更時の処理
  const handleTextChange = (e) => {
    const newText = e.target.value;
    
    // 文字数制限チェック
    if (getCharacterCount(newText) > MAX_TWEET_LENGTH) {
      // 制限を超える場合は更新しない
      return;
    }
    
    setPostText(newText);
    
    // テキストエリアの高さ自動調整
    e.target.style.height = 'auto';
    e.target.style.height = (e.target.scrollHeight) + 'px';
    
    // URLの検出
    detectUrls(newText);
    
    // メンション候補の検索
    const selectionStart = e.target.selectionStart;
    const textBeforeCursor = newText.substring(0, selectionStart);
    
    // @サインの位置を探す
    const lastAtSign = textBeforeCursor.lastIndexOf('@');
    if (lastAtSign !== -1) {
      // @サインと現在のカーソル位置の間にスペースがないか確認
      const textAfterAt = textBeforeCursor.substring(lastAtSign + 1);
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        setMentionQuery(textAfterAt);
        setMentionSelectionStart(lastAtSign);
        setShowMentionDropdown(true);
        calculateMentionDropdownPosition(lastAtSign);
        searchUsers(textAfterAt);
        return;
      }
    }
    
    // メンション候補を閉じる条件
    setShowMentionDropdown(false);
  };

  // 絵文字選択処理
  const handleEmojiSelect = (emoji) => {
    setPostText(prevText => prevText + emoji);
    setShowEmojiPicker(false);
  };

  // GIF選択処理
  const handleGifSelect = (gifUrl) => {
    setPostImage(gifUrl);
    setShowGifDialog(false);
  };

  // 投票オプション追加
  const addPollOption = () => {
    if (pollOptions.length < 4) {
      setPollOptions([...pollOptions, '']);
    }
  };

  // 投票オプション更新
  const handlePollOptionChange = (index, value) => {
    const newOptions = [...pollOptions];
    newOptions[index] = value;
    setPollOptions(newOptions);
  };

  // シフト選択ダイアログを開く
  const handleOpenShiftDialog = () => {
    setShowShiftDialog(true);
  };
  
  // シフト選択ダイアログを閉じる
  const handleCloseShiftDialog = () => {
    setShowShiftDialog(false);
  };
  
  // 日付を選択してシフトを追加
  const handleSelectShiftDate = (date) => {
    setSelectedDate(date);
    
    // 選択された日付のシフトを確認
    const formattedDate = format(date, 'yyyy-MM-dd');
    const shiftsForDate = shiftsData.filter(shift => shift.date === formattedDate);
    
    if (shiftsForDate.length === 0) {
      alert('この日のシフト情報がありません');
      return;
    }
    
    // 日付とシフト情報をペアで保持
    const newSelectedShift = {
      date: formattedDate,
      shifts: shiftsForDate
    };
    
    // すでに選択されている日付か確認
    const alreadySelected = selectedShiftDates.some(item => item.date === formattedDate);
    
    if (alreadySelected) {
      // すでに選択されている場合は削除
      setSelectedShiftDates(prev => prev.filter(item => item.date !== formattedDate));
    } else {
      // 新しく追加
      setSelectedShiftDates(prev => [...prev, newSelectedShift]);
    }
  };
  
  // 選択したシフトをすべて追加して確定
  const handleConfirmShifts = () => {
    // 選択したシフト情報をダイアログを閉じて確定する
    // （テキストには埋め込まず、selectedShiftDatesのみ更新する）
    handleCloseShiftDialog();
  };
  
  // 選択解除
  const handleClearShiftSelection = () => {
    setSelectedShiftDates([]);
  };
  
  // 選択したシフトを削除
  const handleRemoveSelectedShift = (date) => {
    setSelectedShiftDates(prev => prev.filter(item => item.date !== date));
  };

  // 投稿送信処理
  const handleSubmit = async (e) => {
    console.log("=== handleSubmit 開始 ===");
    
    if (e) e.preventDefault();
    
    if (!canPost || loading) {
      console.log("投稿条件チェック失敗: canPost=", canPost, "loading=", loading);
      return;
    }
    
    // 投稿テキストが空かどうかチェック
    const trimmedText = postText.trim();
    if (!trimmedText && !postImage && !postVideo) {
      console.log("投稿内容が空です");
      return;
    }
    
    // 文字数制限チェック
    if (getCharacterCount(trimmedText) > MAX_TWEET_LENGTH) {
      alert(`投稿は${MAX_TWEET_LENGTH}文字以内で入力してください。現在：${getCharacterCount(trimmedText)}文字`);
      return;
    }

    // もし外部URLが含まれていて、まだ警告を表示していない場合
    if (hasExternalUrl && !showUrlWarning) {
      setShowUrlWarning(true);
      return;
    }
    
    try {
      setLoading(true);
      
      // 投稿データの作成（PHP対応 - JSON形式で統一）
      let postData = {
        text: trimmedText
      };
      
      // シフトデータを追加
      if (selectedShiftDates.length > 0) {
        postData.shifts = selectedShiftDates;
      }
      
      // 画像データがある場合（Base64で送信）
      if (postImage) {
        if (postImage.startsWith('data:image')) {
          // ファイルサイズチェック用にBlobを作成
          const blob = await fetch(postImage).then(r => r.blob());
          if (blob.size > 5 * 1024 * 1024) {
            throw new Error('画像ファイルサイズが5MBを超えています');
          }
          
          // ファイル情報を取得
          let fileType = "image/jpeg";
          if (postImage.startsWith('data:image/png')) {
            fileType = "image/png";
          } else if (postImage.startsWith('data:image/gif')) {
            fileType = "image/gif";
          } else if (postImage.startsWith('data:image/webp')) {
            fileType = "image/webp";
          }
          
          console.log('画像データ送信:', {
            type: fileType,
            sizeInMB: (blob.size / (1024 * 1024)).toFixed(2),
            dataUrlLength: postImage.length
          });
          
          // Base64データとして送信
          postData.image_data = postImage;
          postData.image_type = fileType;
        } else {
          postData.image_url = postImage;
        }
      }
      
      // 動画データがある場合（Base64で送信）
      if (postVideo) {
        if (postVideo.startsWith('data:video')) {
          // ファイルサイズチェック用にBlobを作成
          const blob = await fetch(postVideo).then(r => r.blob());
          if (blob.size > 50 * 1024 * 1024) {
            throw new Error('動画ファイルサイズが50MBを超えています');
          }
          
          // ファイル情報を取得
          let fileType = "video/mp4";
          if (postVideo.startsWith('data:video/webm')) {
            fileType = "video/webm";
          } else if (postVideo.startsWith('data:video/mov')) {
            fileType = "video/quicktime";
          } else if (postVideo.startsWith('data:video/avi')) {
            fileType = "video/x-msvideo";
          }
          
          console.log('動画データ送信:', {
            type: fileType,
            sizeInMB: (blob.size / (1024 * 1024)).toFixed(2),
            dataUrlLength: postVideo.length
          });
          
          // Base64データとして送信
          postData.video_data = postVideo;
          postData.video_type = fileType;
        } else {
          postData.video_url = postVideo;
        }
      }
      
      // 投票データがある場合
      if (pollOptions && pollOptions.length >= 2 && pollOptions[0].trim() && pollOptions[1].trim()) {
        const validOptions = pollOptions.filter(opt => opt.trim());
        postData.poll = {
          options: validOptions,
          duration: pollDuration
        };
      }
      
      // 投稿の予約日時があれば追加
      if (scheduledDate) {
        postData.scheduled_at = scheduledDate;
      }
      
      // 位置情報があれば追加
      if (location) {
        postData.location = location;
      }
      
      // APIエンドポイントを決定
      const endpoint = scheduledDate ? '/api/posts/schedule' : '/api/posts';
      console.log("使用エンドポイント:", endpoint);
      
      // 投稿データの内容をデバッグログ出力
      console.log('JSON投稿データ:', postData);
      
      // 認証トークンの確認
      const authToken = localStorage.getItem('authToken');
      console.log("認証トークン:", authToken ? authToken.substring(0, 20) + "..." : "なし");

      console.log("=== API呼び出し開始 ===");
      
      // 一時的にaxiosを直接使用してテスト
      const axios = (await import('axios')).default;
      const fullUrl = `${config.API_BASE_URL}${endpoint}`;
      console.log("リクエストURL:", fullUrl);
      
      // PHP対応のヘッダー設定（JSON統一）
      const headers = {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      };
      
      console.log('リクエストヘッダー:', headers);
      console.log('データ形式: JSON');
      console.log('PostData詳細:', postData);
      
      const requestConfig = {
        headers,
        withCredentials: true,
        timeout: 30000, // 30秒のタイムアウト
        maxContentLength: Infinity,
        maxBodyLength: Infinity
      };
      
      console.log('リクエスト設定:', {
        url: fullUrl,
        method: 'POST',
        headers: requestConfig.headers,
        timeout: requestConfig.timeout
      });
      
      const response = await axios.post(fullUrl, postData, requestConfig);
      
      console.log("=== API呼び出し完了 ===");
      console.log("レスポンス:", response);
      console.log("レスポンス詳細:", {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        data: response.data,
        dataType: typeof response.data,
        dataKeys: response.data && typeof response.data === 'object' ? Object.keys(response.data) : 'not_object'
      });
      
      // 投稿成功時の処理 - 200または201番台のみ成功とみなす
      if (response.status === 201 || response.status === 200) {
        console.log('✅ [POST] Post created successfully:', response);
        
        // メンション通知処理
        if (response.data.mention_notifications && response.data.mention_notifications.length > 0) {
          console.log(`🔔 [MENTION] Processing ${response.data.mention_notifications.length} mention notifications`);
          
          for (const mention of response.data.mention_notifications) {
            try {
              console.log(`🔔 [MENTION] Sending push notification for user: ${mention.receiver_id}, mentioned: @${mention.mentioned_username}`);
              
              // メンション通知を送信
              await apiService.post('/api/notifications/sync-push/mention', {
                receiver_id: mention.receiver_id,
                post_id: mention.post_id,
                mentioned_username: mention.mentioned_username
              });
              
              console.log('✅ [MENTION] Push notification sent successfully');
            } catch (mentionError) {
              console.warn('⚠️ [MENTION] Push notification failed (ignored):', mentionError);
            }
          }
        }
        
        // 投稿データを取得して画面に追加
        let postData = null;
        if (response.data.post) {
          postData = response.data.post;
        } else if (response.data.id) {
          postData = response.data;
        }
        
        // 投稿成功後のフォームリセット
        setPostText('');
        setPostImage('');
        setPostVideo('');
        setPollOptions(['', '']);
        setScheduledDate('');
        setLocation('');
        setSelectedShiftDates([]);
        setShowUrlWarning(false);
        setHasExternalUrl(false);
        
        // テキストエリアの高さをリセット
        if (textAreaRef.current) {
          textAreaRef.current.style.height = 'auto';
        }
        
        // 削除済み投稿IDでないことを確認してから追加
        if (postData && postData.id && isPostIdDeleted(postData.id)) {
          // サーバー側でIDが再利用されている可能性があるため、エラーメッセージを表示
          alert('この投稿IDは既に削除されたIDと重複しています。別の投稿として再送信してください。');
        } else if (postData && postData.id && addPost) {
          // 正常な新規投稿として追加
          addPost(postData);
          console.log('✅ [POST] Post added to timeline:', postData.id);
        }
      } else {
        // 成功ステータス以外の場合はエラーとして処理
        throw new Error(`投稿に失敗しました。ステータスコード: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ [POST] Post creation failed:', error);
      console.error('❌ [POST] Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        headers: error.response?.headers,
        config: error.config
      });
      
      // エラー処理 - ユーザーにエラーを表示
      let errorMessage = '投稿に失敗しました。';
      
      if (error.response) {
        if (error.response.status === 400) {
          // 400エラーの詳細を表示
          const errorData = error.response.data;
          if (errorData?.error) {
            errorMessage = `リクエストエラー: ${errorData.error}`;
          } else if (errorData?.message) {
            errorMessage = `リクエストエラー: ${errorData.message}`;
          } else {
            errorMessage = `リクエストエラー (400): データ形式に問題があります`;
          }
        } else if (error.response.status === 401) {
          errorMessage = '認証エラーが発生しました。再度ログインしてください。';
        } else if (error.response.status === 403) {
          errorMessage = '投稿権限がありません。';
        } else if (error.response.status === 413) {
          errorMessage = 'ファイルサイズが大きすぎます。';
        } else if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        } else {
          errorMessage = `投稿エラー: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'ネットワークエラーが発生しました。インターネット接続を確認してください。';
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // テキスト入力やイメージ変更時にボタンの表示を強制
  useEffect(() => {
    // ボタンが参照されていれば、表示を強制
    if (buttonRef.current) {
      buttonRef.current.style.display = 'inline-flex';
      buttonRef.current.style.visibility = 'visible';
      buttonRef.current.style.opacity = (!postText.trim() && !postImage && !postVideo) ? 0.7 : 1;
    }
  }, [postText, postImage, postVideo]); // テキストまたは画像が変わる度に実行

  // モーダル内表示時にテキストエリアに自動フォーカス
  useEffect(() => {
    if (inModal && textAreaRef.current) {
      setTimeout(() => {
        textAreaRef.current.focus();
      }, 100);
    }
  }, [inModal]);

  // 投稿権限がない場合は何も表示しない
  if (!canPost) {
    return null;
  }

  // ゲストユーザー向け表示
  if (!isLoggedIn) {
    return (
      <div className="tweetBox guestTweetBox">
        <div className="tweetBox__guest">
          <h3>コミュニティに参加しよう！</h3>
          <p>すべての機能を使うにはログインが必要です</p>
          <div className="tweetBox__guestButtons">
            <Button
              variant="contained"
              className="registration-highlight-button"
              onClick={() => navigate('/register')}
            >
              ユーザー登録
            </Button>
            <Button
              variant="outlined"
              className="tweetBox__buttonOutlined"
              onClick={() => navigate('/login')}
            >
              ログイン
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // 圧縮中の表示を追加
  const renderCompressionUI = () => {
    if (!compressing) return null;
    
    return (
      <div className="tweetBox__compressionOverlay">
        <div className="tweetBox__compressionContent">
          <CircularProgress size={40} variant="determinate" value={compressionProgress} />
          <p>動画を圧縮中... {compressionProgress}%</p>
        </div>
      </div>
    );
  };

  // 選択したシフトの表示
  const renderSelectedShifts = () => {
    if (selectedShiftDates.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
          シフトが選択されていません
        </Typography>
      );
    }
    
    // 日付順にソート
    const sortedShifts = [...selectedShiftDates].sort((a, b) => {
      return new Date(a.date) - new Date(b.date);
    });
    
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          選択したシフト:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {sortedShifts.map((item) => {
            // parseISOが日付を正しく解析できない場合の対応
            const dateObj = parseISO(item.date);
            const formattedDate = isValid(dateObj) 
              ? format(dateObj, 'yyyy/MM/dd (EEE)', { locale: ja })
              : item.date;
              
            return (
              <Chip
                key={item.date}
                label={formattedDate}
                onDelete={() => handleRemoveSelectedShift(item.date)}
                color="primary"
                variant="outlined"
                size="small"
              />
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <div className={`tweetBox ${inModal ? 'tweetBox--in-modal' : ''}`}>
      {renderCompressionUI()}
      <form onSubmit={handleSubmit}>
        <div className="tweetBox__input">
          <Avatar 
            className="tweetBox__avatar" 
            src={normalizeImageUrl(userProfile?.avatar || currentUser?.avatar)}
            alt={userProfile?.display_name || currentUser?.display_name || 'ユーザー'}
          >
            {(!userProfile?.avatar && !currentUser?.avatar) && 
              (userProfile?.display_name || currentUser?.display_name || userProfile?.username || currentUser?.username || 'U')?.charAt(0)?.toUpperCase()
            }
          </Avatar>
          <div className="tweetBox__inputContainer" ref={textAreaWrapperRef}>
            <textarea
              ref={textAreaRef}
              value={postText}
              onChange={handleTextChange}
              placeholder="今どうしてる？"
              maxLength={280}
              rows={1}
              className="tweetBox__inputText"
              disabled={!canPost || loading}
            />
            
            {/* メンションドロップダウン */}
            {showMentionDropdown && mentionResults.length > 0 && (
              <div 
                className="tweetBox__mentionDropdown"
                style={{ 
                  top: `${mentionDropdownPosition.top}px`,
                  left: `${mentionDropdownPosition.left}px`
                }}
              >
                {mentionResults.map((user) => (
                  <div 
                    key={user.username} 
                    className="tweetBox__mentionItem"
                    onClick={() => selectMention(user.username)}
                  >
                    <Avatar src={normalizeImageUrl(user.avatar)} className="tweetBox__mentionAvatar" />
                    <div className="tweetBox__mentionUserInfo">
                      <div className="tweetBox__mentionDisplayName">{user.display_name}</div>
                      <div className="tweetBox__mentionUsername">@{user.username}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* シフト情報のプレビュー表示 */}
            {selectedShiftDates.length > 0 && (
              <div className="tweetBox__shiftsPreview">
                {selectedShiftDates.sort((a, b) => new Date(a.date) - new Date(b.date)).map((item) => (
                  <Paper key={item.date} sx={{ p: 1, mb: 1, borderRadius: 1, backgroundColor: '#f5f8fa' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {(() => {
                          const dateObj = parseISO(item.date);
                          return isValid(dateObj) 
                            ? format(dateObj, 'yyyy年MM月dd日 (EEE)', { locale: ja }) 
                            : item.date;
                        })()}のシフト
                      </Typography>
                      <IconButton size="small" onClick={() => handleRemoveSelectedShift(item.date)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box>
                      {item.shifts.map((shift, idx) => (
                        <Typography key={idx} variant="body2" sx={{ mt: 0.5 }}>
                          {shift.start_time} - {shift.end_time}
                          {shift.is_next_day && ' (翌日)'}
                        </Typography>
                      ))}
                    </Box>
                  </Paper>
                ))}
              </div>
            )}

            {postImage && (
              <div className="tweetBox__imagePreview">
                <div 
                  className="tweetBox__cancelButton"
                  onClick={() => setPostImage('')}
                >
                  &times;
                </div>
                <img 
                  src={postImage} 
                  alt="Preview" 
                  className="tweetBox__imagePreviewContent"
                />
              </div>
            )}

            {postVideo && (
              <div className="tweetBox__videoPreview">
                <video 
                  src={postVideo} 
                  controls 
                  controlsList="nodownload nofullscreen" 
                  disablePictureInPicture
                  onContextMenu={(e) => e.preventDefault()}
                  autoPlay={false}
                  muted={true}
                  defaultMuted
                />
                <IconButton className="tweetBox__videoCloseButton" onClick={() => setPostVideo('')}>
                  <CloseIcon />
                </IconButton>
              </div>
            )}

            {location && (
              <div className="tweetBox__locationTag">
                <LocationOnOutlinedIcon fontSize="small" />
                <span>{location}</span>
                <CloseIcon 
                  fontSize="small" 
                  className="tweetBox__removeIcon"
                  onClick={() => setLocation('')}
                />
              </div>
            )}

            {/* 投票設定プレビュー */}
            {!showPollDialog && pollOptions.filter(opt => opt.trim()).length >= 2 && (
              <div className="tweetBox__pollPreview">
                <PollOutlinedIcon fontSize="small" />
                <div className="tweetBox__pollOptions">
                  {pollOptions.filter(opt => opt.trim()).map((option, index) => (
                    <div key={index} className="tweetBox__pollOption">{option}</div>
                  ))}
                  <div className="tweetBox__pollDurationPreview">
                    {pollDuration === "1d" && "1日間"}
                    {pollDuration === "3d" && "3日間"}
                    {pollDuration === "7d" && "7日間"}
                  </div>
                </div>
                <CloseIcon 
                  fontSize="small" 
                  className="tweetBox__removeIcon"
                  onClick={() => {
                    setPollOptions(['', '']);
                    setPollDuration("1d");
                  }}
                />
              </div>
            )}

            {scheduledDate && (
              <div className="tweetBox__scheduleTag">
                <EventNoteOutlinedIcon fontSize="small" />
                <span>投稿予定: {scheduledDate}</span>
                <CloseIcon 
                  fontSize="small" 
                  className="tweetBox__removeIcon"
                  onClick={() => setScheduledDate('')}
                />
              </div>
            )}
          </div>
        </div>
        
        <div className="tweetBox__footer">
          <div className="tweetBox__toolbox">
            {/* 画像アップロードボタン */}
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileUpload}
              disabled={postVideo !== '' || !canPost}
            />
            <InsertPhotoOutlinedIcon 
              className="tweetBox__icon" 
              onClick={() => fileInputRef.current.click()} 
            />

            {/* 動画アップロードボタン */}
            <input
              type="file"
              accept="video/*"
              style={{ display: 'none' }}
              ref={videoInputRef}
              onChange={handleVideoUpload}
              disabled={postImage !== '' || !canPost}
            />
            <IconButton 
              onClick={() => videoInputRef.current.click()}
              className="tweetBox__toolButton"
              disabled={postImage !== '' || !canPost}
              title="動画を追加"
            >
              <VideoCallOutlinedIcon />
            </IconButton>

            {/* GIF選択ボタン - 非表示に */}
            {/* <GifBoxOutlinedIcon 
              className="tweetBox__icon" 
              onClick={() => setShowGifDialog(true)} 
            /> */}

            {/* 投票ボタン */}
            <PollOutlinedIcon 
              className="tweetBox__icon" 
              onClick={() => setShowPollDialog(true)} 
            />

            {/* 絵文字ボタン */}
            <div className="tweetBox__emojiContainer">
              <SentimentSatisfiedAltOutlinedIcon 
                className="tweetBox__icon" 
                onClick={() => setShowEmojiPicker(!showEmojiPicker)} 
              />
              {showEmojiPicker && (
                <div className="tweetBox__emojiPicker">
                  <div className="tweetBox__emojiGrid">
                    {basicEmojis.map((emoji, index) => (
                      <span 
                        key={index} 
                        className="tweetBox__emojiItem"
                        onClick={() => handleEmojiSelect(emoji)}
                      >
                        <span className="emoji">{emoji}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* スケジュール投稿ボタン - 非表示に */}
            {/* <EventNoteOutlinedIcon 
              className="tweetBox__icon" 
              onClick={() => setShowScheduleDialog(true)} 
            /> */}

            {/* 位置情報ボタン */}
            <LocationOnOutlinedIcon 
              className="tweetBox__icon" 
              onClick={() => setShowLocationDialog(true)} 
            />
            
            {/* シフト投稿ボタン - 投稿ユーザーの場合のみ表示 */}
            {isPosterUser && (
              <CalendarMonthIcon
                className="tweetBox__icon"
                onClick={handleOpenShiftDialog}
              />
            )}
          </div>
          
          <div className="tweetBox__buttonContainer">
            <div className="tweetBox__charCounter">
              <span className={`tweetBox__charCount ${isOverLimit ? 'tweetBox__charCount--over' : remainingChars <= 20 ? 'tweetBox__charCount--warning' : ''}`}>
                {remainingChars}
              </span>
            </div>
            <Button
              className={`tweetBox__button ${(!postText.trim() && !postImage && !postVideo) || isOverLimit ? 'tweetBox__buttonDisabled' : ''}`}
              type="submit"
              disabled={loading || (!postText.trim() && !postImage && !postVideo) || isOverLimit}
              ref={buttonRef}
              onClick={(e) => {
                console.log("=== 投稿ボタンクリック ===");
                console.log("Buttonクリックイベント:", e);
                console.log("disabled状態:", loading || (!postText.trim() && !postImage && !postVideo));
                console.log("loading:", loading);
                console.log("postText:", postText);
                console.log("postImage:", postImage);
                console.log("postVideo:", postVideo);
              }}
            >
              {loading ? '投稿中...' : '投稿する'}
            </Button>
          </div>
        </div>
      </form>

      {/* GIF選択ダイアログ */}
      <Dialog open={showGifDialog} onClose={() => setShowGifDialog(false)} maxWidth="md">
        <DialogTitle>
          GIFを選択
          <IconButton 
            aria-label="close" 
            onClick={() => setShowGifDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="gif-search"
            label="GIFを検索"
            type="search"
            fullWidth
            variant="outlined"
            placeholder="キーワードを入力"
          />
          <div className="tweetBox__gifGrid">
            {mockGifs.map((gif, index) => (
              <div 
                key={index} 
                className="tweetBox__gifItem"
                onClick={() => handleGifSelect(gif)}
              >
                <img src={gif} alt={`GIF ${index + 1}`} />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* 投票作成ダイアログ */}
      <Dialog open={showPollDialog} onClose={() => setShowPollDialog(false)}>
        <DialogTitle>投票を作成</DialogTitle>
        <DialogContent>
          {pollOptions.map((option, index) => (
            <TextField
              key={index}
              margin="dense"
              label={`選択肢 ${index + 1}`}
              type="text"
              fullWidth
              value={option}
              onChange={(e) => handlePollOptionChange(index, e.target.value)}
              variant="outlined"
            />
          ))}
          {pollOptions.length < 4 && (
            <Button 
              onClick={addPollOption}
              variant="outlined"
              fullWidth
              sx={{ marginTop: 2 }}
            >
              選択肢を追加
            </Button>
          )}
          <div className="tweetBox__pollDuration">
            <p>投票期間:</p>
            <RadioGroup
              row
              value={pollDuration}
              onChange={(e) => setPollDuration(e.target.value)}
            >
              <FormControlLabel value="1d" control={<Radio />} label="1日" />
              <FormControlLabel value="3d" control={<Radio />} label="3日" />
              <FormControlLabel value="7d" control={<Radio />} label="7日" />
            </RadioGroup>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPollDialog(false)}>キャンセル</Button>
          <Button 
            onClick={() => {
              if (pollOptions.filter(opt => opt.trim()).length >= 2) {
                setShowPollDialog(false);
                // pollDurationの値を確認して適切な形式に変換
                if (typeof pollDuration !== 'string') {
                  setPollDuration(pollDuration.toString() + 'd');
                }
              } else {
                alert('少なくとも2つの選択肢が必要です');
              }
            }}
            variant="contained"
          >
            完了
          </Button>
        </DialogActions>
      </Dialog>

      {/* 位置情報ダイアログ */}
      <Dialog open={showLocationDialog} onClose={() => setShowLocationDialog(false)}>
        <DialogTitle>位置情報を追加</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="location"
            label="位置情報"
            type="text"
            fullWidth
            variant="outlined"
            placeholder="例: 東京, 渋谷区"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLocationDialog(false)}>キャンセル</Button>
          <Button 
            onClick={() => setShowLocationDialog(false)}
            variant="contained"
          >
            追加
          </Button>
        </DialogActions>
      </Dialog>

      {/* スケジュール投稿ダイアログ */}
      <Dialog open={showScheduleDialog} onClose={() => setShowScheduleDialog(false)}>
        <DialogTitle>投稿をスケジュール</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="schedule-date"
            label="日付と時間"
            type="datetime-local"
            fullWidth
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowScheduleDialog(false)}>キャンセル</Button>
          <Button 
            onClick={() => setShowScheduleDialog(false)}
            variant="contained"
          >
            スケジュール
          </Button>
        </DialogActions>
      </Dialog>

      {/* 外部URL警告ダイアログ */}
      <Dialog open={showUrlWarning} onClose={() => setShowUrlWarning(false)}>
        <DialogTitle>外部URLの投稿制限</DialogTitle>
        <DialogContent>
          <p>外部URLを含む投稿はセキュリティ上の理由から制限されています。</p>
          <p>投稿を続けるには、外部URLを削除してください。</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUrlWarning(false)} variant="contained">
            了解
          </Button>
        </DialogActions>
      </Dialog>

      {/* シフト選択ダイアログ */}
      <Dialog
        open={showShiftDialog}
        onClose={handleCloseShiftDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          シフト情報を追加
          <IconButton
            aria-label="close"
            onClick={handleCloseShiftDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loadingShifts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : shiftsData.length === 0 ? (
            <Typography variant="body1" sx={{ py: 2 }}>
              シフト情報がありません。先にシフトを登録してください。
            </Typography>
          ) : (
            <>
              <Box sx={{ my: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
                  <DatePicker
                    label="日付を選択"
                    value={selectedDate}
                    onChange={handleSelectShiftDate}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    inputFormat="yyyy年MM月dd日 (EEE)"
                  />
                </LocalizationProvider>
              </Box>
              
              {renderSelectedShifts()}
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                ※日付を選択すると、その日のシフトが追加されます。同じ日をもう一度選ぶと解除されます。
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearShiftSelection} color="secondary">
            選択解除
          </Button>
          <Button onClick={handleConfirmShifts} color="primary" variant="contained">
            確定
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default TweetBox; 